/**
 * Каталог модулей портала: навигация, этапы разработки и связь с разделами ТЗ.
 * Один источник для меню, страницы «Ход разработки» и проверки прав.
 */

export type ModuleCode =
  | 'inbox'
  | 'calendar'
  | 'portfolio'
  | 'analytics'
  | 'lists'
  | 'reports'
  | 'suppliers'
  | 'ai'
  | 'overview'
  | 'psd'
  | 'estimates'
  | 'budget'
  | 'schedule'
  | 'resources'
  | 'fleet'
  | 'procurement'
  | 'contracts'
  | 'site'
  | 'supervision'
  | 'changes'
  | 'warehouse'
  | 'finance'
  | 'qhse'
  | 'video'
  | 'documents'
  | 'kb'
  | 'norms'
  | 'admin'
  | 'roadmap'
  | 'whatsnew'
  | 'support';

export type NavGroup = 'company' | 'project' | 'system';

export interface PortalModule {
  code: ModuleCode;
  title: string;
  group: NavGroup;
  /** Раздел ТЗ, где описан модуль */
  tzSection?: string;
  /** Этап разработки, на котором модуль появляется в первой версии */
  stage: number;
  /** Дата первой выкладки раздела (ГГГГ-ММ-ДД): раздел выложен раньше своего этапа — пометка «эт. N» снимается */
  releasedAt?: string;
  /** Право, нужное, чтобы видеть модуль */
  permission: string;
  /** Путь в веб-приложении */
  path: string;
  description: string;
}

export const NAV_GROUP_TITLES: Record<NavGroup, string> = {
  company: 'Компания',
  project: 'Проект',
  system: 'Система',
};

export const MODULES: PortalModule[] = [
  {
    code: 'inbox',
    title: 'Ждёт меня',
    group: 'company',
    tzSection: '6.21',
    stage: 3,
    permission: 'project.view',
    path: '/inbox',
    description:
      'Согласования, проверки, оплаты и поручения из всех разделов одним списком: срок, откуда дело, кнопка действия.',
  },
  {
    code: 'calendar',
    title: 'Календарь',
    group: 'company',
    tzSection: '6.21',
    stage: 3,
    permission: 'project.view',
    path: '/calendar',
    releasedAt: '2026-10-19',
    description:
      'Личный календарь: дела со сроком, контроль поручений, вехи проектов, платежи и поставки по графикам договоров; подписка для календаря телефона (ICS).',
  },
  {
    code: 'portfolio',
    title: 'Портфель',
    group: 'company',
    tzSection: '6.1',
    stage: 1,
    permission: 'portfolio.view',
    path: '/',
    description: 'Все проекты и объекты на одном экране, блок «Требует внимания», деньги, сроки и маржа.',
  },
  {
    code: 'analytics',
    title: 'Руководителю',
    group: 'company',
    tzSection: '6.18',
    stage: 12,
    releasedAt: '2026-10-20',
    permission: 'analytics.view',
    path: '/analytics',
    description:
      'Закупки, деньги, сроки и маржа по проектам на одной странице; освоенный объём, мост маржи и отклонения по разделам.',
  },
  {
    // Перед «Отчётами»: крошки /reports/lists/* находят этот раздел первым
    code: 'lists',
    title: 'Списки',
    group: 'company',
    tzSection: '6.18',
    stage: 12,
    releasedAt: '2026-10-21',
    permission: 'lists.view',
    path: '/reports/lists',
    description:
      'Настраиваемые списки разделов: договоры, заявки, замечания, работы графика, бюджет — колонки, отбор, группировка и свои виды; только то, на что есть право.',
  },
  {
    code: 'reports',
    title: 'Отчёты и списки',
    group: 'company',
    tzSection: '6.18',
    stage: 12,
    releasedAt: '2026-10-21',
    permission: 'analytics.view',
    path: '/reports',
    description:
      'Готовые отчёты заказчику и на совещание, сводка руководителю в 08:00, конструктор отчётов, настраиваемые списки и выгрузка в Excel и PDF.',
  },
  {
    code: 'suppliers',
    title: 'Поставщики',
    group: 'company',
    tzSection: '6.12',
    stage: 2,
    permission: 'suppliers.view',
    path: '/suppliers',
    description: 'Справочник поставщиков и подрядчиков: реквизиты, категории, документы, рейтинг, стоп-лист.',
  },
  {
    code: 'ai',
    title: 'Pilot',
    group: 'company',
    stage: 12,
    releasedAt: '2026-10-26',
    permission: 'ai.use',
    path: '/ai',
    description:
      'Pilot — собственная ИИ-модель портала: ответы по данным проектов, разбор документов, сводки.',
  },
  {
    code: 'overview',
    title: 'Обзор',
    group: 'project',
    tzSection: '6.1',
    stage: 1,
    permission: 'project.view',
    path: '/projects',
    description: 'Карточка проекта: объекты, оплаты, вехи, бюджет по разделам, сводка площадки.',
  },
  {
    code: 'psd',
    title: 'ПСД',
    group: 'project',
    tzSection: '6.4',
    stage: 8,
    releasedAt: '2026-10-17',
    permission: 'psd.view',
    path: '/psd',
    description: 'Проектная документация по шифрам и ревизиям, просмотрщик, сравнение ревизий.',
  },
  {
    code: 'estimates',
    title: 'Сметы',
    group: 'project',
    tzSection: '6.5',
    stage: 4,
    releasedAt: '2026-10-17',
    permission: 'estimate.view',
    path: '/estimates',
    description: 'Сметы на нормативной базе РК, версии, KENML 1.0 и 2.01, ресурсная ведомость.',
  },
  {
    code: 'budget',
    title: 'Бюджет и маржа',
    group: 'project',
    tzSection: '6.6',
    stage: 5,
    releasedAt: '2026-10-16',
    permission: 'budget.view',
    path: '/budget',
    description: 'Себестоимость по статьям, варианты исполнения, пять видов маржи, бюджет-график оплат.',
  },
  {
    code: 'schedule',
    title: 'График',
    group: 'project',
    tzSection: '6.2',
    stage: 6,
    releasedAt: '2026-10-17',
    permission: 'schedule.view',
    path: '/schedule',
    description: 'Собственный планировщик: CPM, базовые планы, освоенный объём, диаграмма Ганта.',
  },
  {
    code: 'resources',
    title: 'Ресурсы',
    group: 'project',
    tzSection: '6.3',
    stage: 6,
    releasedAt: '2026-10-17',
    permission: 'resources.view',
    path: '/resources',
    description: 'Люди и техника из графика и норм, вахты, табели, допуски, загрузка.',
  },
  {
    code: 'fleet',
    title: 'Техника',
    group: 'project',
    tzSection: '6.32',
    stage: 6,
    releasedAt: '2026-10-21',
    // Раздел ведёт поток 9 на правах ресурсов: смотрят с resources.view, ведут с resources.edit
    permission: 'resources.view',
    path: '/fleet',
    description:
      'Парк техники: карточки, документы со сроками, путевые листы, топливо и нормы расхода, выработка и стоимость машино-часа.',
  },
  {
    code: 'procurement',
    title: 'Закупки',
    group: 'project',
    tzSection: '6.7',
    stage: 2,
    permission: 'procurement.view',
    path: '/procurement',
    description: 'Заявки, запросы КП, сравнение и выбор поставщика, реестры и заявки на оплату.',
  },
  {
    code: 'contracts',
    title: 'Договоры',
    group: 'project',
    tzSection: '6.8',
    stage: 3,
    permission: 'contracts.view',
    path: '/contracts',
    description: 'Договоры из выбранного КП, согласование, графики поставок и платежей, исполнение.',
  },
  {
    code: 'site',
    title: 'Площадка',
    group: 'project',
    tzSection: '6.9',
    stage: 7,
    releasedAt: '2026-10-19',
    permission: 'site.view',
    path: '/site',
    description: 'План на день, ежедневные отчёты с фото и видео, замечания, приёмка материалов.',
  },
  {
    code: 'supervision',
    title: 'Надзор и вопросы',
    group: 'project',
    tzSection: '6.10',
    stage: 8,
    releasedAt: '2026-10-17',
    permission: 'supervision.view',
    path: '/supervision',
    description: 'Технадзор, авторский надзор, вопросы к проекту, единый реестр замечаний.',
  },
  {
    code: 'changes',
    title: 'Изменения',
    group: 'project',
    tzSection: '6.11',
    stage: 8,
    releasedAt: '2026-10-17',
    permission: 'changes.view',
    path: '/changes',
    description: 'Запросы на изменение, анализ влияния, согласование, новые версии документов.',
  },
  {
    code: 'warehouse',
    title: 'Склад',
    group: 'project',
    tzSection: '6.13',
    stage: 9,
    releasedAt: '2026-10-17',
    permission: 'warehouse.view',
    path: '/warehouse',
    description: 'Склады, каталог ТМЦ, поступление, перемещение, выдача, остатки и резервы, этикетки с QR.',
  },
  {
    code: 'finance',
    title: 'Финансы',
    group: 'project',
    // Первая версия — платёжный календарь и комплекты документов к оплате; кэш-флоу и сверка с 1С — на этапе 9
    stage: 3,
    permission: 'finance.view',
    path: '/finance',
    description:
      'Платёжный календарь компании и проектов с лимитами платежей, комплекты документов к оплате; дальше — кэш-флоу проекта и сверка с 1С.',
  },
  {
    code: 'qhse',
    title: 'Качество и ОТ',
    group: 'project',
    tzSection: '6.16',
    stage: 10,
    releasedAt: '2026-10-20',
    permission: 'qhse.view',
    path: '/qhse',
    description:
      'Замечания и предписания ОТК, ОТ и ПБ, приёмки по чек-листам и конструктор шаблонов; дальше — наряды-допуски, инструктажи, допуски работников.',
  },
  {
    code: 'video',
    title: 'Видеонаблюдение',
    group: 'project',
    tzSection: '6.31',
    stage: 7,
    releasedAt: '2026-10-21',
    permission: 'video.view',
    path: '/video',
    description:
      'Карточки камер площадок, события видеоаналитики с кадром и решением человека, журнал просмотров, положение о видеонаблюдении и согласия на биометрию; видеосервер — на площадке заказчика.',
  },
  {
    code: 'documents',
    title: 'Документы',
    group: 'project',
    stage: 10,
    releasedAt: '2026-10-20',
    permission: 'documents.view',
    path: '/documents',
    description: 'Исходные файлы проекта: загрузка, категории доступа и скачивание.',
  },
  {
    code: 'kb',
    title: 'База знаний',
    group: 'company',
    tzSection: '6.22',
    stage: 12,
    releasedAt: '2026-10-21',
    permission: 'kb.view',
    path: '/kb',
    description:
      'Регламенты, правила расчётов, шаблоны и уроки проектов: статьи с владельцем и проверкой, поиск по словам, просмотр нормативных документов, подписка на изменения.',
  },
  {
    code: 'norms',
    title: 'Нормативная база',
    group: 'system',
    tzSection: '6.5',
    stage: 4,
    releasedAt: '2026-10-17',
    permission: 'norms.view',
    path: '/norms',
    description: 'АГСК-3, ЕСЦ, ЭСН, ССЦ, СЦЭМ, СЦЗТ, индексы НДЦС РК по выпускам.',
  },
  {
    code: 'admin',
    title: 'Администрирование',
    group: 'system',
    stage: 0,
    permission: 'admin.users',
    path: '/admin',
    description: 'Пользователи, приглашения, роли и права, юрлица группы, история изменений.',
  },
  {
    code: 'roadmap',
    title: 'Ход разработки',
    group: 'system',
    stage: 0,
    permission: 'roadmap.view',
    path: '/roadmap',
    description: 'Этапы разработки и статус каждого требования ТЗ.',
  },
  {
    code: 'whatsnew',
    title: 'Что нового',
    group: 'system',
    tzSection: '6.22',
    stage: 0,
    permission: 'project.view',
    path: '/whats-new',
    description: 'Что изменилось в портале после каждой выкладки (БЗ-04).',
  },
  {
    code: 'support',
    title: 'Поддержка',
    group: 'system',
    tzSection: '6.22',
    stage: 12,
    releasedAt: '2026-10-20',
    permission: 'project.view',
    path: '/support',
    description:
      'Помощь к каждой странице, обращения в поддержку со сроками первого ответа и предложения по улучшению портала с голосами и статусами.',
  },
];

/**
 * Последний из этапов, которые сейчас идут. С 1.11.2026 (ТЗ v2.0, ускоренный календарь владельца от 31.10) этапы
 * 4–13 идут одновременно — их ведут девять рабочих чатов; модули этапов дальше этого помечаются в меню как будущие,
 * если раздел ещё не выложен (releasedAt у модуля).
 */
export const CURRENT_STAGE = 13;

/** Первый этап, который идёт параллельно с другими: этапы до него выложены целиком и только дополняются */
export const PARALLEL_FROM_STAGE = 4;

export interface Stage {
  number: number;
  title: string;
  summary: string;
  /** Дата первой выкладки требований этапа на портал (ГГГГ-ММ-ДД) */
  releasedAt?: string;
  /** Что нужно заранее со стороны владельца — шаги нулевого этапа ТЗ v2.0 */
  needs?: string;
}

/**
 * Этапы разработки по ТЗ v2.0 (29.10.2026) и ускоренному календарю владельца (31.10.2026). Этапы 0–3 выложены;
 * 4–13 идут одновременно по чатам, у каждого уже выложена часть (releasedAt — первая выкладка); 14 — приёмка
 * первой очереди 15–19.12.2026 и решение о пилоте 21.12.2026; 15 — после пилота. Пилот — на Railway (решение 28.10).
 */
export const STAGES: Stage[] = [
  {
    number: 0,
    title: 'Платформа',
    summary:
      'Инфраструктура, мультиарендность, вход, юрлица группы, роли и права по юрлицу и проекту, история «кто, что, когда, было → стало», файлы.',
    releasedAt: '2026-10-16',
  },
  {
    number: 1,
    title: 'Портфель и проекты',
    summary:
      'Портфель, карточка проекта с вкладками, объекты, регистрация проекта по Регламенту № 33П, блок «Требует внимания».',
    releasedAt: '2026-10-16',
  },
  {
    number: 2,
    title: 'Закупки',
    summary:
      'Заявки, КП и сравнение по критериям, выбор с пояснением, реестры и заявки на оплату, реестр контрагентов.',
    releasedAt: '2026-10-16',
  },
  {
    number: 3,
    title: 'Договоры',
    summary:
      'Договоры из КП, согласование пятью согласующими, графики поставок и платежей, допсоглашения, «Ждёт меня» и поручения.',
    releasedAt: '2026-10-16',
  },
  {
    number: 4,
    title: 'Сметы и нормативная база',
    summary:
      'Нормативная база РК, локальные и объектные сметы, версии, KENML 1.0 и 2.01, заявка из позиции сметы. Ведёт чат «Сметы и бюджет»; эталоны ПМ-01 и ПМ-02.',
    releasedAt: '2026-10-17',
    needs:
      'ставки и правило округления (шаг 0.4, до 4.11); 13 смет KENML 227-23, выгрузки АВС и бюджет-пакет (шаг 0.5, до 6.11)',
  },
  {
    number: 5,
    title: 'Бюджет и маржа',
    summary:
      'Себестоимость из сметы, варианты исполнения, маржа пяти видов, бюджет-график оплат, финансовый план по приказу № 29П. Ведёт чат «Сметы и бюджет».',
    releasedAt: '2026-10-16',
  },
  {
    number: 6,
    title: 'График и ресурсы',
    summary:
      'Собственное ядро CPM, диаграмма Ганта, базовые планы, ресурсы, вахты и табели, техника. Ведёт чат «График и ресурсы»; ПМ-03 и скорость ПМ-08.',
    releasedAt: '2026-10-17',
    needs: 'эталонные графики заказчика в XER и XML (шаг 0.6, до 13.11)',
  },
  {
    number: 7,
    title: 'Площадка, приложения и внешние кабинеты',
    summary:
      'План на день, отчёты с фото и видео, работа без связи, приложение на телефоне, уведомления, кабинеты поставщиков и заказчика. Ведёт чат «Площадка и телефон»; ПМ-05, ПМ-09, ПМ-14 — статистика площадки с 16.11.',
    releasedAt: '2026-10-16',
    needs:
      'пилотные прораб и представитель заказчика; приложение через браузер или магазины App Store и Google Play (шаг 0.18, до 13.11)',
  },
  {
    number: 8,
    title: 'ПСД, надзор и изменения',
    summary:
      'ПСД по шифрам и ревизиям, технадзор и авторский надзор, вопросы к проекту, запросы на изменение. Ведёт чат «Надзор, ПСД и изменения».',
    releasedAt: '2026-10-17',
  },
  {
    number: 9,
    title: 'Склад и финансы',
    summary:
      'Склады и каталог ТМЦ, приёмка и выдача с телефона, резервы под график, инвентаризация; ДДС, платёжный календарь, акты и авансы, субподряд и обеспечения, контроли закупок и оплат (ЗК-33, ФН-20, ФН-21). Ведут чаты «Закупки, договоры и склады» и «Финансы»; ПМ-04 и ПМ-13.',
    releasedAt: '2026-10-17',
  },
  {
    number: 10,
    title: 'Качество, документы и совещания',
    summary:
      'Предписания, чек-листы, наряды-допуски и инструктажи; письма, шаблоны и печатные формы, журналы работ и исполнительная документация; совещания, протоколы и календарь.',
    releasedAt: '2026-10-17',
    needs: 'шаблоны исполнительной документации и журналов (шаг 0.7, до 13.11)',
  },
  {
    number: 11,
    title: 'Интеграции и обмен с 1С',
    summary:
      'Адаптеры и журнал обмена, справочники и выгрузки 1С, банковские выписки, ЭЦП через NCALayer, проверка контрагентов, Telegram и push, курсы Нацбанка, импорт с Google Диска.',
    releasedAt: '2026-10-17',
    needs:
      'ключи бота Telegram и проверки файлов (шаг 0.9, до 4.11); источник проверки контрагентов, выписки и поля обмена с 1С (шаг В-6, до 13.11)',
  },
  {
    number: 12,
    title: 'Аналитика, Pilot и база знаний',
    summary:
      'Освоенный объём и мост маржи, конструктор отчётов, настройка списков, свод на дату; Pilot — извлечение полей из КП, счетов и накладных (ПМ-10), помощник по данным; помощь на каждой странице, база знаний, поддержка и предложения (ПМ-11). Pilot работает на текущей службе модели; свой сервер с видеокартой отложен (шаг 0.12).',
    releasedAt: '2026-10-16',
    needs: 'набор документов для проверки ПМ-10 и месячный бюджет ИИ (шаги В-4 и 0.17)',
  },
  {
    number: 13,
    title: 'Пилот на Railway, передача и администрирование',
    summary:
      'Пилот работает на Railway (решение владельца 28.10.2026): передача исходного кода и чистая установка на Railway (ПМ-12), копии и проверка восстановления, мониторинг; настройки без программиста, конструктор маршрутов, лимиты, замещения, журнал доступа. Серверы в двух дата-центрах Казахстана и сервер с видеокартой отложены (шаг 0.12).',
    releasedAt: '2026-10-20',
    needs:
      'заключение юриста о персональных данных пилота на Railway — перенос в РК либо согласия и минимизация (шаг 0.15, до 6.11)',
  },
  {
    number: 14,
    title: 'Приёмка первой очереди',
    summary:
      'Критерии ПМ-01…ПМ-14: заморозка маршрутов для внешнего теста 27.11, отчёт теста 11.12, итоговая проверка всех 14 критериев на одной версии 15–19.12.2026, решение о старте пилота 21.12.2026.',
    needs:
      'данные владельца в срок — иначе эталоны ПМ-01…ПМ-04 сдвигаются на столько же; заказ внешнего теста (шаг 0.13, до 13.11)',
  },
  {
    number: 15,
    title: 'Вторая и третья очередь',
    summary:
      'После решения о пилоте: требования 2 и 3 очереди в порядке, который владелец уточняет по итогам пилота; требования первой очереди без чата, если владелец перенесёт их (шаг 0.16); остальное из SIGNAL и мировые практики.',
  },
];

export function moduleByCode(code: ModuleCode): PortalModule {
  const m = MODULES.find((x) => x.code === code);
  if (!m) throw new Error(`Unknown module ${code}`);
  return m;
}
